import { type ValidatorFn } from '@angular/forms';
import {
  type ISetOptionParams,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { IUserRestrictionDay } from '@principle-theorem/principle-core/interfaces';
import {
  DayOfWeek,
  DAYS_OF_WEEK,
  Time24hrType,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface IDayScheduleRow {
  enabled: boolean;
  from: Time24hrType;
  to: Time24hrType;
}

export type UserRestrictionScheduleFormData = Record<
  DayOfWeek,
  IDayScheduleRow
>;

export const DEFAULT_SCHEDULE_FROM: Time24hrType = '09:00';
export const DEFAULT_SCHEDULE_TO: Time24hrType = '17:00';

const atLeastOneDayMessage = 'At least one day should be enabled';
const invalidRangeMessage = 'Each enabled day must start before it ends';

class DayScheduleRowFormGroup extends TypedFormGroup<IDayScheduleRow> {
  constructor(enabled: boolean) {
    super({
      enabled: new TypedFormControl<boolean>(enabled),
      from: new TypedFormControl<Time24hrType>(DEFAULT_SCHEDULE_FROM),
      to: new TypedFormControl<Time24hrType>(DEFAULT_SCHEDULE_TO),
    });
  }
}

export class UserRestrictionScheduleFormGroup extends TypedFormGroup<UserRestrictionScheduleFormData> {
  constructor() {
    const dayControls = DAYS_OF_WEEK.reduce(
      (controls, day) => ({
        ...controls,
        [day]: new DayScheduleRowFormGroup(true),
      }),
      {} as Record<DayOfWeek, DayScheduleRowFormGroup>
    );

    super(dayControls, { validators: validateSchedule() });
  }

  getErrorMessage$(): Observable<string | undefined> {
    return this.valueChanges.pipe(
      startWith(undefined),
      map(() => this._errorMessage())
    );
  }

  getSchedule(): IUserRestrictionDay[] {
    const value = this.getRawValue();
    return DAYS_OF_WEEK.filter((day) => value[day].enabled).map((day) => ({
      day,
      allowedFrom: value[day].from,
      allowedTo: value[day].to,
    }));
  }

  setSchedule(
    schedule: IUserRestrictionDay[],
    options?: ISetOptionParams
  ): void {
    const byDay = new Map(schedule.map((entry) => [entry.day, entry]));
    DAYS_OF_WEEK.forEach((day) => {
      const entry = byDay.get(day);
      this.controls[day].setValue(
        {
          enabled: entry !== undefined,
          from: entry?.allowedFrom ?? DEFAULT_SCHEDULE_FROM,
          to: entry?.allowedTo ?? DEFAULT_SCHEDULE_TO,
        },
        options
      );
    });
  }

  resetToDefault(options?: ISetOptionParams): void {
    DAYS_OF_WEEK.map((day) => {
      this.controls[day].setValue(
        {
          enabled: true,
          from: DEFAULT_SCHEDULE_FROM,
          to: DEFAULT_SCHEDULE_TO,
        },
        options
      );
    });
  }

  private _errorMessage(): string | undefined {
    if (this.hasError('atLeastOneDay')) {
      return atLeastOneDayMessage;
    }
    if (this.hasError('invalidRange')) {
      return invalidRangeMessage;
    }
  }
}

function validateSchedule(): ValidatorFn {
  return (control) => {
    const value = control.value as UserRestrictionScheduleFormData | undefined;
    if (!value) {
      // eslint-disable-next-line no-null/no-null
      return null;
    }

    const enabledDays = DAYS_OF_WEEK.filter((day) => value[day]?.enabled);
    if (!enabledDays.length) {
      return { atLeastOneDay: atLeastOneDayMessage };
    }

    const hasInvalidRange = enabledDays.some(
      (day) => value[day].from >= value[day].to
    );
    if (hasInvalidRange) {
      return { invalidRange: invalidRangeMessage };
    }

    // eslint-disable-next-line no-null/no-null
    return null;
  };
}
